import { Box, Toolbar } from '@mui/material';
import { find, propEq } from 'ramda';
import { useCallback, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useAppDispatch, useAppSelector } from '../../redux/ReduxHooks';
import { applicationSlice } from '../../redux/ApplicationSlice';
import { projectSlice } from '../../redux/ProjectSlice';
import { ApplicationActions } from '../../utils/constants';
import { database } from '../../utils/sensor-data/database';
import CsvDownloadDialog from '../Dialogs/Data/CsvDownloadDialog';
import { buildCsvStreamLabelOptions, buildCsvStreamLabelOptionsFromLayout, mergeCsvStreamLabelOptions } from '../Dialogs/Data/csvStreamLabel';
import { deriveSessionRunIds, resolveHistoricLayout } from './dashboardHistoricUtils';
import { SensorRun } from './dashboardTypes';
import { useLiveGraphs } from './LiveGraphStore';
import DashboardModeBar from './DashboardModeBar';
import { useRegisteredCustomCallableParameters } from './CustomCallableStreamsContext';

const { LIVE, HISTORIC } = ApplicationActions.DashboardViewMode;

const generateFilename = (sensor: string) => [sensor, 'csv'].join('.');

const createDownload = (data: string, sensor: string) => {
  const link = document.createElement('a');
  const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  try {
    link.setAttribute('href', url);
    link.setAttribute('download', generateFilename(sensor));
    link.click();
  } finally {
    URL.revokeObjectURL(url);
  }
};

const DashboardToolbar = () => {
  const dispatch = useAppDispatch();
  const [openCSV, setOpenCsv] = useState(false);
  const [chosenSensors, setChosenSensors] = useState<string[]>([]);

  const identifier = useAppSelector((state) => state.project.loadedProject?.id);
  const projectCode = useAppSelector((state) => state.project.loadedProject?.program?.code ?? '');
  const driveUnitPorts = useAppSelector((state) => state.application.driveUnitPorts);
  const dashboardViewMode = useAppSelector((state) => state.application.dashboardViewMode);
  const runDate = useAppSelector((state) => state.application.runDate);
  const graphs = useLiveGraphs();

  const registeredCustomCallableParametersFromCode = useRegisteredCustomCallableParameters();

  const allData = useLiveQuery(() => {
    if (!identifier) return [];
    return database.runData.where('programId').equals(identifier).toArray();
  }) as Array<SensorRun>;

  const isHistoric = dashboardViewMode === HISTORIC;
  const sessions = useMemo(() => deriveSessionRunIds(allData), [allData]);
  const historicLayout = useMemo(() => resolveHistoricLayout(allData ?? [], runDate), [allData, runDate]);
  const csvStreamLabelOptions = useMemo(() => {
    const live = buildCsvStreamLabelOptions(driveUnitPorts, projectCode, registeredCustomCallableParametersFromCode);
    if (isHistoric && historicLayout.length) {
      const saved = buildCsvStreamLabelOptionsFromLayout(historicLayout, registeredCustomCallableParametersFromCode);
      return mergeCsvStreamLabelOptions(live, saved);
    }
    return live;
  }, [driveUnitPorts, historicLayout, isHistoric, projectCode, registeredCustomCallableParametersFromCode]);

  const handleCloseCsv = useCallback(() => {
    setOpenCsv(false);
  }, []);

  const generateCSV = useCallback(() => {
    chosenSensors.forEach((sensor) => {
      const item = find(propEq(sensor, 'key'), graphs);
      if (item) {
        if (!item.data?.length) return;
        const keys = Object.keys(item.data[0]);
        const data = item.data.map((datum) => keys.map((k) => datum[k]).join(','));
        createDownload([keys.join(','), data.join('\n')].join('\n'), sensor);
      }
    });
    setOpenCsv(false);
  }, [chosenSensors, graphs]);

  const onSessionChange = useCallback(
    (runId: number | null) => {
      dispatch(applicationSlice.actions.onSetRunDate(runId));
    },
    [dispatch]
  );

  const onOpenCsv = useCallback(() => setOpenCsv(true), []);

  const onCopyHistoricLayout = useCallback(() => {
    dispatch(projectSlice.actions.onSaveLayout(historicLayout));
    dispatch(applicationSlice.actions.onSaveLayout(historicLayout));
    dispatch(applicationSlice.actions.onSetDashboardViewMode(LIVE));
  }, [dispatch, historicLayout]);

  return (
    <>
      <Box
        component="header"
        sx={{
          flexShrink: 0,
          width: '100%',
          minWidth: 0,
          bgcolor: 'background.editor',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar
          variant="dense"
          sx={{
            minHeight: 56,
            px: 1,
            py: 1,
            width: '100%',
            minWidth: 0,
            flexWrap: 'nowrap',
          }}
        >
          <DashboardModeBar
            runDate={runDate}
            sessions={sessions}
            onSessionChange={onSessionChange}
            onOpenCsv={onOpenCsv}
            onCopyHistoricLayout={onCopyHistoricLayout}
          />
        </Toolbar>
      </Box>
      <CsvDownloadDialog
        open={openCSV}
        handleClose={handleCloseCsv}
        sensors={graphs || []}
        chosenSensors={chosenSensors}
        setChosenSensors={setChosenSensors}
        generateCSV={generateCSV}
        csvStreamLabelOptions={csvStreamLabelOptions}
      />
    </>
  );
};

export default DashboardToolbar;
